import { createContext, type ReactNode, useContext, useEffect, useMemo, useState } from 'react';
import type { Preferences } from '../../../core/ports/preferences-repository';
import { PreferencesService } from '../../../core/services/preferences-service';
import { LocalStoragePreferencesRepository, LOCAL_PREFERENCES_KEY } from '../../../storage/implementations/localstorage-preferences-repository';
import { t as translate, type Locale, type MessageKey } from './index';

const preferencesService = new PreferencesService(new LocalStoragePreferencesRepository());

interface LocaleContextValue {
  locale: Locale;
  setLocale(locale: Locale): void;
  t(key: MessageKey): string;
}

const LocaleContext = createContext<LocaleContextValue>({
  locale: 'zh-TW',
  setLocale: () => undefined,
  t: (key) => translate('zh-TW', key),
});

export function LocaleProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(() => readStoredLocale());

  useEffect(() => {
    let active = true;

    void preferencesService.getLocale().then((storedLocale) => {
      if (active) {
        setLocaleState(storedLocale);
      }
    });

    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);

  const value = useMemo<LocaleContextValue>(() => ({
    locale,
    setLocale(nextLocale) {
      setLocaleState(nextLocale);
      void preferencesService.setLocale(nextLocale);
    },
    t(key) {
      return translate(locale, key);
    },
  }), [locale]);

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

export function useLocale(): LocaleContextValue {
  return useContext(LocaleContext);
}

function readStoredLocale(): Locale {
  if (typeof localStorage === 'undefined') {
    return 'zh-TW';
  }

  try {
    const raw = localStorage.getItem(LOCAL_PREFERENCES_KEY);
    if (!raw) {
      return 'zh-TW';
    }

    const parsed = JSON.parse(raw) as Partial<Preferences>;
    return parsed.locale === 'en' ? 'en' : 'zh-TW';
  } catch {
    return 'zh-TW';
  }
}